/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useMemo } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { frame } from '../flight/state.ts'
import { HASH } from '../shaders/chunks.ts'
import { mulberry32 } from '../prng.ts'
import { glsl, WHITE } from '../palette.ts'

// ARTIFACTS beat: a few clean white geometric solids drifting upward among the sheets
// (the white-cubes-into-sky reference). 48 instanced boxes, one draw call.

const VERT = /* glsl */ `
attribute float aSeed;
uniform float uTime, uLift, uFog;
uniform vec3 uBg;
varying vec3 vCol;
${HASH}
mat3 rotY(float a){ float c=cos(a),s=sin(a); return mat3(c,0.0,s, 0.0,1.0,0.0, -s,0.0,c); }
void main() {
  vec3 r = hash31(aSeed);
  vec3 r2 = hash31(aSeed + 2.2);
  float s = (0.4 + r2.x * 1.2) * smoothstep(0.0, 0.35, uLift);
  float rise = uLift * (4.0 + r.y * 26.0);
  vec3 wp = vec3((r.x * 2.0 - 1.0) * 40.0, 1.0 + rise + sin(uTime * 0.3 + aSeed) * 0.4, -10.0 + r.z * 90.0);
  mat3 R = rotY(uTime * 0.1 + aSeed);
  vec3 tp = R * (position * s) + wp;
  vec4 mv = viewMatrix * vec4(tp, 1.0);
  gl_Position = projectionMatrix * mv;
  vec3 n = normalize(R * normal);
  float l = 0.82 + 0.18 * max(dot(n, normalize(vec3(0.35, 1.0, 0.5))), 0.0);
  vec3 col = ${glsl(WHITE)} * l;
  float dist = length(mv.xyz);
  float fo = 1.0 - exp(-uFog * uFog * dist * dist);
  col = mix(col, uBg, fo);
  vCol = col;
}
`
const FRAG = /* glsl */ `
varying vec3 vCol;
void main() { gl_FragColor = vec4(vCol, 1.0); }
`

export function Solids() {
  const { geometry, material } = useMemo(() => {
    const N = 48
    const base = new THREE.BoxGeometry(1, 1, 1)
    const geometry = new THREE.InstancedBufferGeometry()
    geometry.index = base.index
    geometry.setAttribute('position', base.attributes.position)
    geometry.setAttribute('normal', base.attributes.normal)
    const rand = mulberry32(808)
    const seeds = new Float32Array(N)
    for (let i = 0; i < N; i++) seeds[i] = rand() * 120
    geometry.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seeds, 1))
    geometry.instanceCount = N
    const material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uTime: { value: 0 },
        uLift: { value: 0 },
        uFog: { value: 0.016 },
        uBg: { value: new THREE.Color() },
      },
    })
    return { geometry, material }
  }, [])

  useFrame((st) => {
    const b = frame.beats
    material.uniforms.uTime.value = st.clock.elapsedTime
    material.uniforms.uLift.value = b.lift
    material.uniforms.uFog.value = b.fog
    ;(material.uniforms.uBg.value as THREE.Color).copy(frame.bg)
  })

  return <mesh geometry={geometry} material={material} frustumCulled={false} />
}
